<template>
  <div v-if="showPrompt" class="pwa-install-prompt">
    <div class="prompt-content">
      <div class="prompt-icon">
        <i class="fas fa-download"></i>
      </div>
      <h3>Установить приложение</h3>
      <p>Установите Mancu1 Call для более удобного использования и получения push-уведомлений</p>
      <div class="prompt-actions">
        <button @click="handleInstall" class="install-btn">
          <i class="fas fa-download"></i>
          Установить
        </button>
        <button @click="handleDismiss" class="dismiss-btn">
          Позже
        </button>
      </div>
      <button @click="handleNeverShow" class="never-show-btn">
        Больше не показывать
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Компонент для показа popup установки PWA
 */

import { ref, onMounted, onUnmounted } from 'vue';
import { showInstallPrompt, isPWAInstalled, isInstallPromptAvailable } from '../utils/pwa';

// ==================== СОСТОЯНИЕ ====================

const showPrompt = ref(false);
let beforeInstallPromptEvent: any = null;

// ==================== ЛОГИКА ====================

/**
 * Проверяет, нужно ли показывать промпт
 */
function shouldShowPrompt(): boolean {
  // Не показываем если уже установлено
  if (isPWAInstalled()) {
    return false;
  }
  
  // Проверяем localStorage для флага "никогда не показывать"
  const neverShow = localStorage.getItem('pwa-install-never-show');
  if (neverShow === 'true') {
    return false;
  }
  
  // Проверяем время последнего показа
  const lastShown = localStorage.getItem('pwa-install-last-shown');
  if (lastShown) {
    const lastShownTime = parseInt(lastShown, 10);
    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000; // 24 часа
    
    // Показываем не чаще раза в день
    if (now - lastShownTime < oneDay) {
      return false;
    }
  }
  
  return isInstallPromptAvailable();
}

/**
 * Показывает промпт с задержкой
 */
function showPromptWithDelay(): void {
  // Показываем промпт через 3 секунды после загрузки
  setTimeout(() => {
    if (shouldShowPrompt()) {
      showPrompt.value = true;
      localStorage.setItem('pwa-install-last-shown', Date.now().toString());
    }
  }, 3000);
}

/**
 * Обрабатывает установку
 */
async function handleInstall(): Promise<void> {
  try {
    const success = await showInstallPrompt();
    if (success) {
      showPrompt.value = false;
      console.log('✅ PWA установка инициирована');
    }
  } catch (error) {
    console.error('❌ Ошибка установки PWA:', error);
  }
}

/**
 * Закрывает промпт
 */
function handleDismiss(): void {
  showPrompt.value = false;
  // Показываем снова через день
  localStorage.setItem('pwa-install-last-shown', Date.now().toString());
}

/**
 * Никогда не показывать промпт
 */
function handleNeverShow(): void {
  localStorage.setItem('pwa-install-never-show', 'true');
  showPrompt.value = false;
}

/**
 * Обрабатывает событие beforeinstallprompt
 */
function handleBeforeInstallPrompt(event: Event): void {
  // Предотвращаем стандартное поведение
  event.preventDefault();
  
  // Сохраняем событие для использования позже
  beforeInstallPromptEvent = event;
  (window as any).deferredPrompt = event;
  
  console.log('📱 PWA install prompt доступен');
  
  // Показываем промпт если нужно
  if (shouldShowPrompt()) {
    showPromptWithDelay();
  }
}

// ==================== LIFECYCLE ====================

onMounted(() => {
  // Если уже установлено, не показываем
  if (isPWAInstalled()) {
    return;
  }
  
  // Слушаем событие beforeinstallprompt
  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  
  // Показываем промпт с задержкой если событие уже было
  if (isInstallPromptAvailable()) {
    showPromptWithDelay();
  }
});

onUnmounted(() => {
  window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
});
</script>

<style scoped>
.pwa-install-prompt {
  position: fixed;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  animation: slideUp 0.3s ease;
  max-width: 400px;
  width: calc(100% - 40px);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.prompt-content {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.98), rgba(50, 50, 50, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(20px);
}

.prompt-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pulse 2s ease-in-out infinite;
}

.prompt-icon i {
  font-size: 28px;
  color: #fff;
}

@keyframes pulse {
  0%, 100% { opacity: 0.9; }
  50% { opacity: 1; }
}

.prompt-content h3 {
  margin: 0 0 12px 0;
  font-size: 1.25rem;
  color: #fff;
  font-weight: 600;
  text-align: center;
}

.prompt-content p {
  margin: 0 0 20px 0;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  font-size: 0.875rem;
  text-align: center;
}

.prompt-actions {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.install-btn,
.dismiss-btn {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.install-btn {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: #fff;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.install-btn:hover {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
  transform: translateY(-2px);
}

.install-btn:active {
  transform: translateY(0);
}

.dismiss-btn {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.dismiss-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

.never-show-btn {
  width: 100%;
  padding: 8px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.75rem;
  cursor: pointer;
  transition: color 0.2s ease;
}

.never-show-btn:hover {
  color: rgba(255, 255, 255, 0.8);
}

/* Мобильная адаптация */
@media (max-width: 768px) {
  .pwa-install-prompt {
    bottom: 80px;
    width: calc(100% - 20px);
  }

  .prompt-content {
    padding: 20px;
  }

  .prompt-content h3 {
    font-size: 1.125rem;
  }

  .prompt-content p {
    font-size: 0.8125rem;
  }

  .prompt-actions {
    flex-direction: column;
  }

  .install-btn,
  .dismiss-btn {
    width: 100%;
  }
}
</style>

